import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { useStreetBeatChatLegacy as useStreetBeatChat } from 'shared/sdk-compat/useStreetBeatChatLegacy';

import PortfolioConfigData from 'config/portfolio-config.json';
import { AssistantService } from 'shared/services/AssistantService';
import { CustomDataArtifactRecord } from 'types/AssistantTypes';
import { ef_data } from '../FinecoConfigData';
import DistribuzioneTipologiaContainer from '../components/DistribuzioneTipologiaContainer';
import FinecoPortfolioTitle from '../components/FinecoPortfolioTitle';
import { Section } from '../components/Section';
import AnalisiRischioRendimentoAttesoContainer from '../components/analisi-rischio-e-rendimento-atteso/AnalisiRischioRendimentoAttesoContainer';
import AnalisiStrumentiContainer from '../components/analisi-strumenti/AnalisiStrumentiContainer';
import AssetAllocationImplicitaContainer from '../components/asset-allocation-implicita/AssetAllocationImplicitaContainer';
import BacktestContainer from '../components/backtest/BacktestContainer';
import CaratteristicheDiPortafoglioContainer from '../components/caratteristiche-di-portafoglio/CaratteristicheDiPortafoglioContainer';
import PortfolioCommentoCompare from '../components/commento/PortfolioCommentoCompare';
import ComposizioneDelPortafoglioContainer from '../components/composizione-del-portafoglio/ComposizioneDelPortafoglioContainer';
import DistribuzioneDeiProventiContainer from '../components/distribuzione-proventi/DistribuzioneDeiProventiContainer';
import EfficientFrontierCompareContainer from '../components/frontiera-efficiente/EfficientFrontierCompareContainer';
import IndicatoriFinanziariContainer from '../components/indicatori-finanziari/IndicatoriFinanziariContainer';
import ScomposizionePortafolioContainer from '../components/scomposizione-di-portafoglio/ScomposizionePortafolioContainer';
import DistribuzioneEsgContainer from '../components/sostenibilita/components/DistribuzioneEsgContainer';
import useThreadsStore from 'shared/store/threadsStore';
import { FinecoPortfolioCompareButtons } from './components/FinecoPortfolioCompareButtons';
import { isFinecoPortfolioArtifact } from 'utils/portfolioUtils';
import { ComparisonLabelsContext, ComparisonLabels } from './ComparisonLabelsContext';
import { createLogger } from 'shared/logger';
import styles from 'pages/assistant/components/artifacts/components/fineco-portfolio/finecoPortfolio.common.module.scss';

const log = createLogger('PortfolioCompare');

type Props = {
  data: CustomDataArtifactRecord;
  artifact: any;
};

export const FinecoPortfolioCompare: React.FC<Props> = memo(({ data, artifact }) => {
  const { t } = useTranslation();
  const params = useParams();
  const threadId = params.threadId;

  const cardRef = useRef<HTMLDivElement>(null);
  const stories = useThreadsStore((state) => state.stories);
  const { messages: chatMessages } = useStreetBeatChat();

  // Extract portfolio IDs directly from the comparison artifact data
  const { portfolioId1, portfolioId2 } = useMemo(() => {
    return {
      portfolioId1: data?.portfolio_1,
      portfolioId2: data?.portfolio_2,
    };
  }, [data]);

  const comparisonLabels: ComparisonLabels | null = data?.portfolio_1_label
    ? {
        portfolio1: data.portfolio_1_label,
        portfolio2: data.portfolio_2_label,
        portfolio1Short: data.portfolio_1_short_label,
        portfolio2Short: data.portfolio_2_short_label,
        portfolio1Perc: `${data.portfolio_1_label} (%)`,
        portfolio2Perc: `${data.portfolio_2_label} (%)`,
      }
    : null;

  // Extract commentoAlPortafoglio flags from original portfolio artifacts in chat messages
  const { commentoAlPortafoglio1, commentoAlPortafoglio2 } = useMemo(() => {
    let flag1 = false;
    let flag2 = false;

    chatMessages?.forEach((msg: any) => {
      msg.artifacts?.forEach((art: any) => {
        if (isFinecoPortfolioArtifact(art)) {
          const artData = art.custom_data?.data as any;
          if (artData?.portfolio_id === portfolioId1) {
            flag1 = Boolean(artData?.commento_al_portafoglio);
          }
          if (artData?.portfolio_id === portfolioId2) {
            flag2 = Boolean(artData?.commento_al_portafoglio);
          }
        }
      });
    });

    return { commentoAlPortafoglio1: flag1, commentoAlPortafoglio2: flag2 };
  }, [chatMessages, portfolioId1, portfolioId2]);

  const [reportData, setReportData] = useState<any>(null);
  const [reportData2, setReportData2] = useState<any>(null);
  const [artifactData, setArtifactData] = useState<any>(null);
  const [artifactData2, setArtifactData2] = useState<any>(null);

  // Date range state for backtest
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [backtestLoading, setBacktestLoading] = useState<boolean>(false);
  const datesInitialized = useRef(false);

  useEffect(() => {
    fetchData();
  }, []);
  // Helper to format date for API
  const formatDateParam = (date: Date): string => {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };

  const fetchData = async () => {
    try {
      // Fetch data using portfolio IDs from the comparison artifact
      if (portfolioId1 && portfolioId2) {
        const [data1, data2] = await Promise.all([
          AssistantService.fetchReportData(portfolioId1),
          AssistantService.fetchReportData(portfolioId2),
        ]);

        setArtifactData(data1.data);
        setArtifactData2(data2.data);

        setReportData(data1);
        setReportData2(data2);

        // Compute timeline intersection from both portfolios' backtest data
        const dates1 =
          data1?.analisi_backtest?.simulazione_andamento_portafoglio?.map((item: any) => new Date(item.date)) || [];
        const dates2 =
          data2?.analisi_backtest?.simulazione_andamento_portafoglio?.map((item: any) => new Date(item.date)) || [];

        if (dates1.length > 0 && dates2.length > 0) {
          const sorted1 = [...dates1].sort((a: Date, b: Date) => a.getTime() - b.getTime());
          const sorted2 = [...dates2].sort((a: Date, b: Date) => a.getTime() - b.getTime());

          // Intersection: latest start, earliest end
          const intersectionStart = new Date(Math.max(sorted1[0].getTime(), sorted2[0].getTime()));
          const intersectionEnd = new Date(
            Math.min(sorted1[sorted1.length - 1].getTime(), sorted2[sorted2.length - 1].getTime()),
          );

          // Only set dates if intersection is valid
          if (intersectionStart <= intersectionEnd) {
            setStartDate(intersectionStart);
            setEndDate(intersectionEnd);
          }
        }
      } else {
        log.warn('no portfolio IDs available for comparison');
      }
    } catch (error) {
      log.error('fetch comparison report data failed', { error });
    }
  };

  // Refetch data when dates change
  useEffect(() => {
    // Skip the initial render to avoid double-fetching
    if (!datesInitialized.current) {
      datesInitialized.current = true;
      return;
    }

    const fetchDataWithDateRange = async () => {
      if (portfolioId1 && portfolioId2) {
        setBacktestLoading(true);
        try {
          const queryParams: { backtest_start?: string; backtest_end?: string } = {};
          if (startDate) {
            queryParams.backtest_start = formatDateParam(startDate);
          }
          if (endDate) {
            queryParams.backtest_end = formatDateParam(endDate);
          }

          const [data1, data2] = await Promise.all([
            AssistantService.fetchReportData(
              portfolioId1,
              Object.keys(queryParams).length > 0 ? queryParams : undefined,
            ),
            AssistantService.fetchReportData(
              portfolioId2,
              Object.keys(queryParams).length > 0 ? queryParams : undefined,
            ),
          ]);

          setArtifactData(data1.data);
          setArtifactData2(data2.data);
          setReportData(data1);
          setReportData2(data2);
        } catch (error) {
          log.error('fetch comparison report data (date range) failed', { error });
        } finally {
          setBacktestLoading(false);
        }
      }
    };

    fetchDataWithDateRange();
  }, [portfolioId1, portfolioId2, startDate, endDate]);

  const sectionsConfig = useMemo(
    () => ({
      efficientFrontierContainer: {
        label: '',
        component: <EfficientFrontierCompareContainer ef_data={ef_data} data1={reportData} data2={reportData2} />,
        isOpen: true,
        showFoldingTitle: false,
      },
      statsContainer: {
        label: '',
        component: (
          <CaratteristicheDiPortafoglioContainer
            data={artifactData}
            data2={artifactData2}
            label={PortfolioConfigData.stats.label}
          />
        ),
        isOpen: true,
        showFoldingTitle: false,
      },
      indicatoriFinanziariContainer: {
        label: '',
        component: (
          <IndicatoriFinanziariContainer
            reportData={reportData}
            reportData2={reportData2}
            label={PortfolioConfigData.indicatori_finanziari.label}
          />
        ),
        isOpen: true,
        showFoldingTitle: false,
      },
      distribuzioneTipologiaStrumentiContainer: {
        label: '',
        component: <DistribuzioneTipologiaContainer reportData={reportData} reportData2={reportData2} />,
        isOpen: true,
        showFoldingTitle: false,
      },
      portfolio: {
        label: PortfolioConfigData.portfolio.label,
        component: <ComposizioneDelPortafoglioContainer reportData={reportData} reportData2={reportData2} />,
        isOpen: true,
        showFoldingTitle: false,
      },
      caratteristicaDiPortafoglio: {
        label: PortfolioConfigData.caratteristiche_di_portafoglio.label,
        component: <DistribuzioneDeiProventiContainer reportData={reportData} reportData2={reportData2} />,
        isOpen: false,
        showFoldingTitle: true,
      },
      assetAllocationImplicita: {
        label: PortfolioConfigData.implicit_asset_allocation.label,
        component: <AssetAllocationImplicitaContainer reportData={reportData} reportData2={reportData2} />,
        isOpen: false,
        showFoldingTitle: true,
      },
      scomposizionePortafoglio: {
        label: PortfolioConfigData.scomposizione_portafoglio.label,
        component: <ScomposizionePortafolioContainer reportData={reportData} reportData2={reportData2} />,
        isOpen: false,
        showFoldingTitle: true,
      },
      analisi_rischio_rendimento_atteso: {
        label: PortfolioConfigData.analisi_rischio_rendimento_atteso.label,
        component: <AnalisiRischioRendimentoAttesoContainer reportData={reportData} reportData2={reportData2} />,
        isOpen: false,
        showFoldingTitle: true,
      },
      ...(reportData?.analisi_backtest?.simulazione_andamento_portafoglio?.length > 0 &&
        reportData2?.analisi_backtest?.simulazione_andamento_portafoglio?.length > 0 && {
          backtestContainer: {
            label: PortfolioConfigData.backtest.label,
            component: (
              <BacktestContainer
                reportData={reportData}
                reportData2={reportData2}
                startDate={startDate}
                endDate={endDate}
                onStartDateChange={setStartDate}
                onEndDateChange={setEndDate}
                backtestLoading={backtestLoading}
              />
            ),
            isOpen: false,
            showFoldingTitle: true,
          },
        }),
      analisi_strumenti: {
        label: PortfolioConfigData.analisi_strumenti.label,
        component: <AnalisiStrumentiContainer reportData={reportData} reportData2={reportData2} />,
        isOpen: false,
        showFoldingTitle: true,
      },
      distribuzioneEsg: {
        label: PortfolioConfigData.sostenibilita.label,
        component: <DistribuzioneEsgContainer reportData={reportData} reportData2={reportData2} />,
        isOpen: false,
        showFoldingTitle: true,
      },
      ...((commentoAlPortafoglio1 || commentoAlPortafoglio2) && {
        portfolioCommento: {
          label: 'Commento al portafoglio',
          component: (
            <PortfolioCommentoCompare
              threadId={threadId}
              portfolio1Id={data.portfolio_1}
              portfolio2Id={data.portfolio_2}
            />
          ),
          isOpen: false,
          showFoldingTitle: true,
        },
      }),
    }),

    [
      reportData,
      reportData2,
      artifactData,
      artifactData2,
      artifact,
      ef_data,
      stories,
      commentoAlPortafoglio1,
      commentoAlPortafoglio2,
      startDate,
      endDate,
      backtestLoading,
    ],
  );
  const [openSections, setOpenSections] = useState(() => {
    return Object.fromEntries(Object.entries(sectionsConfig).map(([key, { isOpen }]) => [key, isOpen]));
  });

  const toggleSection = useCallback((section: string) => {
    setOpenSections((prev) => ({ ...prev, [section]: !prev[section] }));
  }, []);

  //** return jsx */
  if (!reportData) {
    return <div className={styles.loadingPortfolio}>{t('portafoglioCompare.loadingPortfolioCompare')}</div>;
  }

  return (
    <ComparisonLabelsContext.Provider value={comparisonLabels}>
      <div className={styles.portfolioMainContainer} ref={cardRef}>
        <div className={styles.portfolioContainer}>
          <div className={styles.portfolioContainerInner}>
            <div className={styles.portfolioContainerInnerInner}>
              <FinecoPortfolioTitle title={t('portafoglioCompare.PortfolioTitle')} />

              {Object.entries(sectionsConfig).map(([sectionKey, { label, component, showFoldingTitle }]) => {
                if (component.props.children) {
                  const shouldRender = React.Children.toArray(component.props.children).some((child) => child !== null);
                  if (!shouldRender) return null;
                }
                return (
                  <React.Fragment key={sectionKey}>
                    {(sectionKey === 'assetAllocationImplicita' ||
                      sectionKey === 'indicatoriFinanziariContainer' ||
                      sectionKey === 'caratteristicaDiPortafoglio' ||
                      sectionKey === 'analisi_rischio_rendimento_atteso' ||
                      sectionKey === 'distribuzioneEsg' ||
                      sectionKey === 'portfolioCommento' ||
                      sectionKey === 'analisi_strumenti' ||
                      sectionKey === 'scomposizionePortafoglio') && <div className="force-page-break"></div>}
                    <div className={styles.sectionContainer}>
                      <Section
                        showFoldingTitle={showFoldingTitle}
                        toggleSection={toggleSection}
                        openSections={openSections}
                        reportData={reportData}
                        label={label}
                        section={sectionKey}
                      >
                        <div className={styles.sectionComponentContainer}>{component}</div>
                      </Section>
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
            <div
              className="print-button-container"
              style={{
                marginTop: '30px',
                display: 'flex',
                gap: '10px',
                flexWrap: 'wrap',
                marginLeft: 'auto',
                justifyContent: 'flex-end',
              }}
            >
              <FinecoPortfolioCompareButtons
                threadId={threadId}
                portfolioId1={portfolioId1}
                portfolioId2={portfolioId2}
                countervalueTotal1={reportData?.countervalue?.total}
                countervalueTotal2={reportData2?.countervalue?.total}
                commentoAlPortafoglio1={commentoAlPortafoglio1}
                commentoAlPortafoglio2={commentoAlPortafoglio2}
                startDate={startDate}
                endDate={endDate}
              />
            </div>
          </div>
        </div>
        <div id={`lastAdvisorMessageFinecoPortfolio${artifact.portfolio_id}`} className={styles.bottom} />
      </div>
    </ComparisonLabelsContext.Provider>
  );
});
